import React from 'react';
import { useParams, useLocation, useHistory } from "react-router-dom";
import Grid from '@mui/material/Grid';
import { Typography, Divider } from '@mui/material';
import Box from '@mui/material/Box';
import { Item } from '../../styles';

import { fetchPost } from '../../services/GettingData';
import ErrorDialog from '../../components/ErrorDialog';
import Header from '../../components/HeaderComponent';
import ProgressControl from '../../components/ProgressControl';
import NotAuthorizedError from '../../schemas/Exception/NotAuthorizedError';
import IRPeaks from '../../schemas/Spectrum/IRPeaks';
import { Item2 } from '../../styles';

interface SpectrumData {
    spectrum_id: string;
    title: string;
    description?: string;
    compound?: any;
    peaks: number[];
    fit_score: number;
    main_wavenumbers?: string;
    molecule_svg?: string;
    compound_name?: string;
    compound_id?: string;
}

const IRSpectrumCompare: React.FC = () => {
    const { spectrumId } = useParams<{ spectrumId: string }>();
    const location = useLocation<{ spectrumData?: SpectrumData; originalSpectrum?: any; queryPeaks?: IRPeaks }>();
    const history = useHistory();

    const [isLoading, setLoading] = React.useState<boolean>(false);
    const [isDialogOpened, setDialogOpened] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [spectrumData, setSpectrumData] = React.useState<SpectrumData | null>(null);
    const [spectrumImage, setSpectrumImage] = React.useState<string | null>(null);
    const closeErrorDialog = () => setDialogOpened(false);

    const showError = (errorMessage: string) => {
        setErrorMessage(errorMessage);
        setDialogOpened(true);
    };

    // Load spectrum data when component mounts
    React.useEffect(() => {
        if (location.state && (location.state as { spectrumData?: SpectrumData }).spectrumData) {
            const spectrumData = (location.state as { spectrumData?: SpectrumData }).spectrumData;
            setSpectrumData(spectrumData);

            if (location.state.queryPeaks)
                generateCompareSpectrumImage(location.state.queryPeaks, spectrumData);
            else
               generateDrawSpectrumImage();
        } else {
            showError("No spectrum ID or data provided. Unable to display comparison.");
        }
    }, [spectrumId, location.state]);


    const generateDrawSpectrumImage = async () => {

        setLoading(true);
        try {
            const responsePreview = await fetchPost(
                '/ir-spectrum/' + spectrumId + '/preview',
                JSON.stringify({}),
                true,
                true,
                undefined,
                '',
                3
            );
            const buffer = await responsePreview.arrayBuffer();
            const blob = new Blob([buffer], { type: 'image/png' });
            const objectUrl = URL.createObjectURL(blob);
            setSpectrumImage(objectUrl);
        } catch (e) {
            if (e instanceof NotAuthorizedError) {
                history.push({ pathname: '/login', state: { backTo: '/ir-spectrum-result', welcomeMessage: true } })
            }
            else {
                console.error('Error generating spectrum image:', e);
                showError('Failed to generate spectrum visualization.');
            }
        } finally {
            setLoading(false);
        }
    };


    const generateCompareSpectrumImage = async (peaks: IRPeaks, spectrumData: SpectrumData) => {
        if (!peaks) return;

        setLoading(true);
        try {

            const responsePreview = await fetchPost(
                '/ir-spectrum/' + spectrumData.spectrum_id + '/compare',
                JSON.stringify(peaks),
                true,
                true,
                undefined,
                '',
                3
            );
            const buffer = await responsePreview.arrayBuffer();
            const blob = new Blob([buffer], { type: 'image/png' });
            const objectUrl = URL.createObjectURL(blob);
            setSpectrumImage(objectUrl);
        } catch (e) {
            if (e instanceof NotAuthorizedError) {
                history.push({ pathname: '/login', state: { backTo: '/ir-spectrum-result', welcomeMessage: true } })
            }
            else {
                console.error('Error generating spectrum image:', e);
                showError('Failed to generate spectrum visualization.');
            }
        } finally {
            setLoading(false);
        }
    };


    return (
        <Grid container spacing={0} className='main-frame'>
            <ProgressControl isLoading={isLoading} showTime={false} />
            <ErrorDialog
                isDialogOpened={isDialogOpened}
                errorMessage={errorMessage}
                onClose={closeErrorDialog}
            />
            <Grid container>
                <Item><div style={{ height: '2em' }}></div></Item>
            </Grid>

            <Grid item xs={12}>
                <Header title={location.state.queryPeaks ? 'IR Spectrum Comparison' : 'IR Spectrum'} helpAddress='help#ircompare' showLogin={true} />
            </Grid>

            <Grid item xs={12}>
                <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
                </Typography>

            </Grid>

            <Grid container spacing={2} style={{ padding: '20px' }}>
                {/* Spectrum Details */}
                <Grid item xs={12} md={4}>
                    <Item2>
                        <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 'bold' }}>
                        </Typography>

                        {spectrumData ? (
                            <div>
                                <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 'bold' }}></Typography>
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    minHeight: '200px'
                                }}>
                                    <div style={{ width: "200px", position: "relative", cursor: "pointer" }}
                                        onClick={() => history.push(`/exact-search/${spectrumData.compound_id}`)}

                                        dangerouslySetInnerHTML={{
                                            __html: spectrumData.molecule_svg ?
                                                spectrumData.molecule_svg
                                                    .replace(/width="[^"]*"/, 'width="100%"')
                                                    .replace(/height="[^"]*"/, 'height="100%"') :
                                                ''
                                        }}
                                    />
                                </Box>

                                <Box>

                                    <Grid container spacing={2} sx={{ textAlign: 'left' }}>
                                        {/* Title Row */}
                                        <Grid item xs={12}>
                                            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>
                                                Title:
                                            </Typography>
                                            <Typography>
                                                {spectrumData.title || "IR Spectrum"}
                                            </Typography>
                                            <Divider sx={{ my: 2 }} /> {/* Horizontal divider with margin */}
                                        </Grid>

                                        {/* Main Wavenumbers Row */}
                                        <Grid item xs={4}>
                                            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>
                                                Main Wavenumbers:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography variant="body2">
                                                {spectrumData.main_wavenumbers ||
                                                    (Array.isArray(spectrumData.peaks) ?
                                                        spectrumData.peaks.slice(0, 5).join(', ') +
                                                        (spectrumData.peaks.length > 5 ? '...' : '') :
                                                        'N/A'
                                                    )}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Divider sx={{ my: 2 }} /> {/* Horizontal divider with margin */}
                                        </Grid>

                                        {/* Fit Score Row */}
                                        <Grid item xs={4}>
                                            <Typography align="left" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                Fit Score:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography variant="body2">
                                                {typeof spectrumData.fit_score === 'number' ?
                                                    spectrumData.fit_score :
                                                    'N/A'}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Divider sx={{ my: 2 }} /> {/* Horizontal divider with margin */}
                                        </Grid>
                                    </Grid>
                                </Box></div>
                        ) : !isLoading && (
                            <Typography variant="body1" color="text.secondary">
                                No spectrum data available
                            </Typography>
                        )}
                    </Item2>

                </Grid>

                <Grid item xs={12} md={8}>
                    {spectrumImage ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                mb: 2
                            }}
                            className='mol-container'>
                            <div className='graph-container' style={{ maxWidth: '1200px', width: '100%' }}>
                                <img
                                    className='graph-qc'
                                    src={spectrumImage}
                                    style={{
                                        width: '100%',
                                        height: 'auto'
                                    }}
                                    alt="Spectrum Visualization"
                                />
                            </div>
                        </Box>
                    ) : (
                        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
                            No spectrum visualization available
                        </Typography>
                    )}

                </Grid>
            </Grid>
        </Grid>
    );
};

export default IRSpectrumCompare;